import { useStore } from '@nanostores/react';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { ArtifactSnapshotActions } from '~/components/chat/ArtifactSnapshotActions';
import { Collapsible } from '~/components/ui/Collapsible';
import { ShellCodeBlock } from '~/components/ui/ShellCodeBlock';
import { TextShimmer } from '~/components/ui/TextShimmer';
import { isBuildCommand } from '~/lib/persistence/frameworks';
import { importStore } from '~/lib/projects';
import {
  BaseShellAction,
  ImportAction,
  PipeAction,
  StartAction,
  type ActionStatus,
  type ActionType,
  type BoltAction,
} from '~/lib/runtime/actions';
import { workbenchStore, type ArtifactState } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { compileTimeUnreachable } from '~/utils/unreachable';
import { addWorkDir } from '~/utils/workDir';

interface ArtifactProps {
  id: string;
  component?: string;
  autoOpen?: boolean;
  scrollToBottom?: ScrollToBottom;
}

const LONG_RUNNING_ACTIONS = new Set<ActionType>(['shell', 'start']);

export const Artifact = memo(({ id, component, autoOpen = true, scrollToBottom }: ArtifactProps) => {
  const userToggledActions = useRef(false);
  const [showActions, setShowActions] = useState(autoOpen);

  const artifacts = useStore(workbenchStore.artifacts);

  const artifact = artifacts[id];

  // subscribe to action changes, this will cause this component to re-render
  useStore(artifact.runner.stateUpdates);

  const actions = [...artifact.runner.actions.values()];

  const toggleActions = useCallback((showActions: boolean) => {
    userToggledActions.current = true;

    setShowActions(showActions);
  }, []);

  useEffect(() => {
    if (autoOpen && actions.length && !showActions && !userToggledActions.current) {
      setShowActions(true);
    }
  }, [actions, showActions, autoOpen]);

  if (component === 'importing_project') {
    return <ImportingProject artifact={artifact} />;
  }

  if (component === 'publish_application') {
    return <PublishingProject artifact={artifact} />;
  }

  return (
    <Collapsible
      open={showActions}
      scrollToBottom={scrollToBottom}
      onToggle={toggleActions}
      data-artifact-id={artifact.id}
      className="artifact"
      titleClassName="bg-bolt-elements-artifacts-background"
      toggleButtonClassName="bg-bolt-elements-artifacts-background"
      toggleButtonPosition="left"
      title={<div className="p-5 pl-0 grow text-bolt-elements-textPrimary font-medium">{artifact.title}</div>}
      actionButtons={<ArtifactSnapshotActions />}
    >
      <AnimatePresence>
        {actions.length > 0 && (
          <div className="actions p-5 text-left bg-bolt-elements-actions-background rounded-b-lg">
            <ActionList actions={actions} />
          </div>
        )}
      </AnimatePresence>
    </Collapsible>
  );
});

const actionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const ImportingProject = ({ artifact }: { artifact: ArtifactState }) => {
  const updates = useStore(artifact.runner.stateUpdates);
  const [importAction] = [...artifact.runner.actions.values()] as [ImportAction];
  const actions = useStore(importAction.actions);
  const [importProjectAction, installAction] = actions;
  const [installStatus, setInstallStatus] = useState<ActionStatus | undefined>();

  useEffect(() => {
    if (!installAction) {
      return undefined;
    }

    const dispose = installAction.status.subscribe((status) => {
      setInstallStatus(status);
    });

    return () => {
      dispose();
    };
  }, [actions, updates]);

  const hasCompleted = importProjectAction.status.get() === 'complete' && installStatus === 'complete';

  if (hasCompleted) {
    return "I've successfully imported your project. I'm ready to assist you with analyzing and improving your code.";
  }

  return (
    <TextShimmer
      className="[--base-color:var(--bolt-elements-textTertiary)] dark:[--base-color:var(--bolt-elements-textSecondary)] [--base-gradient-color:var(--bolt-elements-textPrimary)]"
      duration={1.5}
      spread={2}
    >
      Importing your project, this will just take a moment&hellip;
    </TextShimmer>
  );
};

const PublishingProject = ({ artifact }: { artifact: ArtifactState }) => {
  const actions = [...artifact.runner.actions.values()];
  const action = actions.find((item) => item.type === 'deploy' || item.type === 'build-and-deploy');
  const status = action?.status.get();
  const isRunning = ['pending', 'running'].includes(status ?? '');

  if (isRunning) {
    return (
      <TextShimmer
        className="[--base-color:var(--bolt-elements-textTertiary)] dark:[--base-color:var(--bolt-elements-textSecondary)] [--base-gradient-color:var(--bolt-elements-textPrimary)]"
        duration={1.5}
        spread={2}
      >
        Publishing your site, this will just take a moment&hellip;
      </TextShimmer>
    );
  }

  return null;
};

interface ActionListProps {
  actions: BoltAction[];
}

const ActionItem = memo(({ action, isLast }: { action: BoltAction; isLast: boolean }) => {
  const currentView = useStore(workbenchStore.currentView);
  const selectedFile = useStore(workbenchStore.selectedFile);
  const importInfo = useStore(importStore);
  const content = useStore(action.content);
  const status = useStore(action.status);
  const actionDetails = useStore(action.actionDetails);

  const { type } = action;

  const isFileAction = action.type === 'file';

  const isEditorVisible = currentView === 'code';

  const isPreviewVisible = currentView === 'preview';
  const isPreviewCommand = action instanceof StartAction;

  const isSelected =
    (isPreviewVisible && isPreviewCommand) ||
    (isFileAction && isEditorVisible && selectedFile === addWorkDir(action.filePath));

  const isFocusable = !isSelected && (isPreviewCommand || isFileAction);

  return (
    <motion.li
      variants={actionVariants}
      initial="hidden"
      animate="visible"
      transition={{
        duration: 0.2,
        ease: cubicEasingFn,
      }}
    >
      <button
        className={classNames('group select-none flex gap-1.5 text-sm bg-transparent text-left', {
          'text-bolt-elements-item-contentAccent': isSelected,
          'hover:underline': isFocusable,
          'pointer-events-none': !isFocusable,
          'pb-1': action instanceof BaseShellAction || type === 'import-project',
        })}
        onClick={() => {
          if (isPreviewCommand) {
            workbenchStore.currentView.set('preview');
          } else if (type === 'file') {
            workbenchStore.currentView.set('code');
            workbenchStore.setSelectedFile(action.filePath);
          }
        }}
      >
        <div className="flex items-center gap-1.5">
          <div className={classNames('text-lg', getIconColor(status))}>
            {status === 'running' ? (
              <>
                {!LONG_RUNNING_ACTIONS.has(action.type) || isBuildCommand(action.content.value) ? (
                  <div className="i-svg-spinners:90-ring-with-bg" />
                ) : (
                  <div className="i-ph:terminal" />
                )}
              </>
            ) : status === 'pending' || status === 'skipped' ? (
              <div className="i-ph:circle-duotone" />
            ) : status === 'complete' ? (
              <div className="i-ph:check" />
            ) : status === 'failed' || status === 'aborted' ? (
              <div className="i-ph:x" />
            ) : null}
          </div>
          {getActionLabel(action)}
        </div>

        {actionDetails && (
          <span>
            <code
              className={classNames(
                'select-text bg-bolt-elements-artifacts-inlineCode-background px-1.5 py-1 rounded-md break-all',
                {
                  'text-bolt-elements-artifacts-inlineCode-text': !isSelected,
                },
              )}
            >
              {actionDetails}
            </code>
          </span>
        )}
      </button>
      {type === 'import-project' && importInfo.statusMessage.length > 0 && (
        <div className="ml-6 mt-1 text-sm text-bolt-elements-textSecondary">{importInfo.statusMessage}</div>
      )}
      {(action instanceof BaseShellAction || type === 'build') && content && (
        <ShellCodeBlock
          className={classNames('border border-bolt-elements-borderColor overflow-hidden rounded-lg w-full mt-1', {
            'mb-3.5': !isLast,
          })}
          code={content}
        />
      )}
      {type === 'deploy' && content && (
        <div className="ml-6 mt-1 text-sm text-bolt-elements-textSecondary">
          <a href={content} target="_blank">
            Open website
          </a>
        </div>
      )}
    </motion.li>
  );
});

const ActionList = memo(({ actions }: ActionListProps) => {
  const [allActions, setAllActions] = useState<BoltAction[]>([]);

  useEffect(() => {
    return workbenchStore.subscribeToActions(setAllActions);
  }, []);

  /**
   * Only show the first running start action at a time.
   * Because the StartAction is automatically appended to all artifacts that
   * don't already contain a start action themselves, we only want to show it
   * in later artifacts when the previous start action has finished.
   */
  const filteredActions = useMemo(() => {
    const firstRunningStart = allActions.find((action) => {
      return action.type === 'start' && (action.status.value === 'running' || action.status.value === 'pending');
    });

    const filteredActions = actions.filter((action) => {
      if (action.type !== 'start') {
        return true;
      }

      if (action.status.value === 'skipped') {
        return false;
      }

      if (action.status.value === 'running' || action.status.value === 'pending') {
        return action === firstRunningStart;
      }

      return true;
    });

    return filteredActions.length ? filteredActions : actions;
  }, [actions, allActions]);

  return (
    <ul className="list-none space-y-2.5">
      {filteredActions.map((action, index) => {
        const isLast = index === filteredActions.length - 1;

        return <BaseActionItem action={action} isLast={isLast} key={action.id} />;
      })}
    </ul>
  );
});

interface BaseActionItemProps {
  action: BoltAction;
  isLast: boolean;
}

function BaseActionItem({ action, isLast }: BaseActionItemProps) {
  if (action instanceof PipeAction) {
    return <PipeActionItem action={action} isLast={isLast} />;
  }

  return <ActionItem action={action} isLast={isLast} />;
}

interface PipeActionItemProps {
  action: PipeAction;
  isLast: boolean;
}

function PipeActionItem({ action, isLast }: PipeActionItemProps) {
  const pipeActions = useStore(action.actions);

  return (
    <>
      {pipeActions.map((action, childIndex) => {
        const isLastChild = childIndex === pipeActions.length - 1;

        return <BaseActionItem key={action.id} action={action} isLast={isLast && isLastChild} />;
      })}
    </>
  );
}

function getIconColor(status: ActionStatus) {
  switch (status) {
    case 'skipped':
    case 'pending': {
      return 'text-bolt-elements-textTertiary';
    }
    case 'running': {
      return 'text-bolt-elements-loader-progress';
    }
    case 'complete': {
      return 'text-bolt-elements-icon-success';
    }
    case 'aborted': {
      return 'text-bolt-elements-textSecondary';
    }
    case 'failed': {
      return 'text-bolt-elements-icon-error';
    }
    default: {
      return undefined;
    }
  }
}

function getActionLabel(action: BoltAction) {
  const type = action.type;

  switch (type) {
    case 'file': {
      return action.actionType;
    }
    case 'supabase': {
      if (action.actionType === 'oauth') {
        return 'Connect to Supabase';
      }

      if (action.actionType === 'create-project') {
        return 'Create project';
      }

      if (action.actionType === 'setup-auth') {
        return 'Setup authentication';
      }

      if (action.actionType === 'create-database') {
        return 'Create database';
      }

      if (action.actionType === 'existing-database') {
        return 'Connecting to Supabase';
      }

      if (action.actionType === 'finalize') {
        return 'Finalize setup';
      }

      if (action.actionType === 'edge-functions') {
        return 'Deploying Edge functions';
      }

      compileTimeUnreachable(action.actionType);
    }
    case 'import-project': {
      return 'Import';
    }
    case 'install': {
      return 'Install dependencies';
    }
    case 'start': {
      return 'Start application';
    }
    case 'shell': {
      return 'Run command';
    }
    case 'import-template': {
      return 'Create initial files';
    }
    case 'build': {
      return 'Build application';
    }
    case 'deploy': {
      return 'Publish to';
    }
    case 'fetch-figma-frame': {
      return 'Fetching from Figma';
    }
    case 'generate-figma-code': {
      return 'Generate code';
    }
    case 'upload-figma-assets': {
      return 'Upload assets';
    }
    case 'github-sync': {
      return 'Write changes';
    }
    case 'github-clone': {
      return 'Sync with repository';
    }

    // those case are intentionally ignored
    case 'import':
    case 'build-and-deploy': {
      return null;
    }

    default: {
      compileTimeUnreachable(type);
    }
  }

  return null;
}
